<template>
  <div class="chartStatDiv" :id="idChart"></div>
</template>

<script>
import * as am4core from '@amcharts/amcharts4/core'
import * as am4charts from '@amcharts/amcharts4/charts'
import am4themesAnimated from '@amcharts/amcharts4/themes/animated'
import { mapGetters } from 'vuex'

am4core.useTheme(am4themesAnimated)

export default {
  name: 'chart',
  props: {
    questionnaire: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    /**
     * @typedef {Object} ChartXYOptions
     * @property {Object} bullet - Initie les points représentant la valeur du jour
     * @property {Object} bulletHover - Objet portant les options des points quand on les survole
     * @property {Object} chart - Initie le grapgique
     * @property {Object} dateXAxis - Crée l'axe des X avec les dates
     * @property {Object} dateXAxis.axis - Objet de AmChart qui porte la configuration
     * @property {Object} sliderButton - Image au centre du bouton de zoom
     * @property {Object} valueYAxis - Crée l'axe des Y avec le nombre d'itération terminée dans la journée
     * @property {Object} valueYAxis.axis - Objet de AmChart qui porte la configuration
     * @type {ChartXYOptions}
     */
    chart: {
      bullet: null,
      bulletHover: null,
      chart: null,
      dateXAxis: {
        axis: null
      },
      sliderButton: null,
      valueYAxis: {
        axis: null
      }
    },
    /**
     * @type {Object} series - Objet représentant la version graphique des résultats des différentes itérations
     * */
    series: null,
    /**
     * @typedef {Object} SettingsChartXY
     * @property {Object} bulletHover - Options quand on survole les points de valeur
     * @property {Object} bulletHover.scaleValue - Valeur d'aggrandissement quand on survole les points
     * @property {String} inputDateFormat - Format de la date d'entrée
     * @property {String} principalColor - Couleur principale définie pour l'ensemble du graphique
     * @property {String} series - Options des series
     * @property {String} series.itemNameX - Nom de la clé dans le tableau des valeurs pour l'axe des X
     * @property {String} series.itemNameY - Nom de la clé dans le tableau des valeurs pour l'axe des Y
     * @type {SettingsChartXY}
     */
    settings: {
      bulletHover: {
        scaleValue: 1.5
      },
      inputDateFormat: 'yyyy-MM-dd',
      principalColor: '#307373',
      series: {
        itemNameX: 'date',
        itemNameY: 'value'
      }
    }
  }),
  computed: {
    ...mapGetters('Dashboard', [
      'questionnaires'
    ]),
    idChart () {
      return `chartStatDiv${this.questionnaire.id}`
    }
  },
  beforeDestroy () {
    if (this.chart.chart) this.chart.chart.dispose()
  },
  async mounted () {
    /** Initialisation du graphique */
    this.chart.chart = am4core.create(this.idChart, am4charts.XYChart)

    /** Insertion des valeurs dans le graphique */
    this.chart.chart.data = this.questionnaire.iterations.chartData

    /** Indique au graphique le format d'entrée de la date */
    this.chart.chart.dateFormatter.inputDateFormat = this.settings.inputDateFormat

    /** Initialisation des axes */
    this.chart.dateXAxis.axis = this.chart.chart.xAxes.push(new am4charts.DateAxis())
    this.chart.valueYAxis.axis = this.chart.chart.yAxes.push(new am4charts.ValueAxis())

    /** Création de la série de valeur */
    this.series = this.chart.chart.series.push(new am4charts.LineSeries())
    this.series.dataFields.dateX = this.settings.series.itemNameX
    this.series.dataFields.valueY = this.settings.series.itemNameY
    this.series.stroke = am4core.color(this.settings.principalColor)

    /** Création de la bulle d'information qui s'affiche au survol */
    this.series.tooltipText = '{value}'
    this.series.strokeWidth = 2
    this.series.minBulletDistance = 15
    this.series.tooltip.background.cornerRadius = 20
    this.series.tooltip.background.strokeOpacity = 0
    this.series.tooltip.pointerOrientation = 'vertical'
    this.series.tooltip.label.minWidth = 40
    this.series.tooltip.label.minHeight = 40
    this.series.tooltip.label.textAlign = 'middle'
    this.series.tooltip.label.textValign = 'middle'

    /** Initialisation des points représentant les valeurs du graphique */
    this.chart.bullet = this.series.bullets.push(new am4charts.CircleBullet())
    this.chart.bullet.circle.strokeWidth = 2
    this.chart.bullet.circle.radius = 4
    this.chart.bullet.circle.fill = am4core.color(this.settings.principalColor)

    this.chart.bulletHover = this.chart.bullet.states.create('hover')
    this.chart.bulletHover.properties.scale = this.settings.bulletHover.scaleValue

    /**
     * Initialisation des curseurs qui donnent les valeurs (date et nombre d'itération)
     * au niveau des axes et création des pointillés pour les relier.
     */
    this.chart.chart.cursor = new am4charts.XYCursor()
    this.chart.chart.cursor.behavior = 'panXY'
    this.chart.chart.cursor.xAxis = this.chart.dateXAxis.axis
    this.chart.chart.cursor.snapToSeries = this.series
  }
}
</script>

<style lang="scss" scoped>
  .chartStatDiv {
    width: 100%;
    height: 500px;
  }
</style>
